/**
 * @file AppShell.tsx
 * @description Общая обёртка страниц admin-кабинета (phase08.5 — shadcn rewrite).
 *
 * Переписан с MUI Drawer/AppBar на Tailwind grid + base-ui Sheet для mobile.
 * Mobile-first: `< md` (768px) — sidebar становится slide-over (Sheet),
 * `≥ md` — fixed 240px рядом с контентом.
 *
 * API сохранён — `AppShell.Sidebar / SidebarHeader / SidebarNav / SidebarFooter /
 * Header / Content` + `useAppShell()` hook.
 *
 * phase19.29 — параметры каркаса (по умолчанию — как раньше, кабинет магазина):
 *  - `pinSidebarAt` — с какой ширины боковая панель закреплена: `md` (магазин)
 *    или `lg` (платформа: на планшете 768–1023 — шторка, как на телефоне);
 *  - `headerSize="tall"` — шапка 56 / 64 / 72px (телефон / планшет / с lg)
 *    под заголовок страницы в две строки; `default` — прежние 64px.
 * В контексте, кроме `isMobile` (панель не закреплена), есть `isPhone` —
 * ширина телефона (< 768) независимо от порога панели.
 *
 * phase19.31 — `banner`: полоса над шапкой на всю ширину, закреплённая
 * (полоса «вход под сотрудником», SC24). Её высота (`--shell-top`, 48px)
 * сдвигает вниз шапку, боковую панель и содержимое — полоса не уезжает при
 * прокрутке и ничего не перекрывает.
 *
 * phase19.31 — фон кабинета (SC65, SC66 «шёлковые ленты»): `Content` рисует первым ребёнком
 * `<main>` слой `CabinetBackdrop` (закреплён относительно окна, под
 * содержимым); на страницах «на всю ширину» (`fluid`) фона нет. Шапка —
 * непрозрачная поверхность темы (Б12: под «стеклом» просвечивал текст).
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { Sheet, SheetContent } from "./sheet";
import { CabinetBackdrop } from "../brand/CabinetBackdrop";
import { BELOW_LG_QUERY, PHONE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

/** Высота шапки (sticky). Совпадает с min-h-16 = 64px. */
const HEADER_HEIGHT_PX = 64;
/**
 * Ширина закреплённой боковой панели — 264px в rem (phase19.31, замечание
 * владельца: на 240 обрезались «Требуют внимания», «Транспортные
 * компании»). Общая для кабинетов магазина и платформы; в rem — чтобы
 * панель росла вместе с увеличенным шрифтом браузера (TD-217).
 */
const SIDEBAR_WIDTH = "16.5rem";
const CONTENT_MAX_WIDTH_PX = 1600;
/** Высота закреплённой полосы над шапкой (вход под сотрудником). */
const BANNER_HEIGHT = "3rem";
/** Отступ сверху под полосу — наследуемая переменная каркаса. */
const SHELL_TOP = "var(--shell-top, 0px)";
/**
 * Боковые и вертикальные отступы колонки содержимого. Одна константа на
 * содержимое и на выровненную шапку (`Header aligned`, phase19.29): левый
 * край названия в шапке совпадает с левым краем содержимого на любой ширине.
 * `px + py` даёт тот же отступ, что прежний `p-4 sm:p-6 md:p-8`.
 */
const CONTENT_GUTTER_X = "px-4 sm:px-6 md:px-8";
const CONTENT_GUTTER_Y = "py-4 sm:py-6 md:py-8";

/** С какой ширины боковая панель закреплена. */
export type SidebarPin = "md" | "lg";
/** Высота шапки: прежние 64px или «высокая» под двухстрочный заголовок. */
export type HeaderSize = "default" | "tall";

/**
 * Классы высоты шапки и отступа содержимого под неё — реестром. `vars` —
 * та же высота переменной `--shell-header-h` (phase19.31, О4): по ней и по
 * `--shell-top` (полоса входа под сотрудником) липкие элементы страниц
 * считают свой верх — `top-[var(--shell-sticky-top)]` — и не уезжают ни под
 * шапку, ни под полосу.
 */
const HEADER_SIZE_CLASS: Record<HeaderSize, { header: string; content: string; vars: string }> = {
  default: { header: "h-16", content: "pt-16", vars: "[--shell-header-h:4rem]" },
  tall: {
    header: "h-14 md:h-16 lg:h-18",
    content: "pt-14 md:pt-16 lg:pt-18",
    vars: "[--shell-header-h:3.5rem] md:[--shell-header-h:4rem] lg:[--shell-header-h:4.5rem]",
  },
};

/** Верх липкого элемента страницы: полоса входа под сотрудником + шапка. */
const STICKY_TOP_VAR = "[--shell-sticky-top:calc(var(--shell-top,0px)+var(--shell-header-h))]";

interface AppShellContextValue {
  /** Боковая панель не закреплена (шторка): < md у магазина, < lg у платформы. */
  isMobile: boolean;
  /** Ширина телефона (< 768) — независимо от порога панели. */
  isPhone: boolean;
  headerSize: HeaderSize;
  /** Открыт ли Sheet-sidebar на mobile. */
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  /**
   * Fluid layout — страница занимает всю ширину контентной зоны
   * (без 1600px max-width). Включается через `useFluidLayout()`
   * на странице (Kanban, dashboards, etc).
   */
  fluid: boolean;
  setFluid: (value: boolean) => void;
  /**
   * Без шёлкового фона при обычной ширине (phase19.16 SB78: экраны с
   * предпросмотром сайта). Включается через `usePlainBackdrop()` (use-plain-backdrop.ts).
   */
  plain: boolean;
  setPlain: (value: boolean) => void;
  /**
   * Меню кабинета в шторке на любой ширине (phase19.16 Р1-6а, §7.16: в
   * конструкторе сайта боковое меню спрятано, открывается кнопкой ≡).
   * `menuInDrawer` — итог: шторка из-за узкого экрана или по просьбе
   * страницы. Включается через `useDrawerMenu()` (use-shell-mode.ts).
   */
  menuInDrawer: boolean;
  setDrawerMenu: (value: boolean) => void;
  /** Без полей контентной зоны: страница сама раскладывает всю площадь (конструктор). */
  bleed: boolean;
  setBleed: (value: boolean) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  if (!ctx) {
    throw new Error("useAppShell() должен быть вызван внутри <AppShell>");
  }
  return ctx;
}

export function useAppShellOptional(): AppShellContextValue | null {
  return useContext(AppShellContext);
}


// ─── Slots ────────────────────────────────────────────────────────────────────

interface SlotProps {
  children: ReactNode;
}

function SidebarHeader({ children }: SlotProps) {
  return <div className="px-4 py-5 shrink-0">{children}</div>;
}

function SidebarNav({ children }: SlotProps) {
  const { menuInDrawer, closeSidebar } = useAppShell();
  return (
    <nav
      // Метка для темы: тонкая полоса прокрутки в цвет панели (console-rules.css).
      data-slot="sidebar-nav"
      onClick={() => {
        if (menuInDrawer) closeSidebar();
      }}
      className="flex-1 overflow-y-auto px-2 scrollbar-thin"
    >
      {children}
    </nav>
  );
}

interface SidebarFooterProps extends SlotProps {
  /**
   * Без внутренних отступов (phase19.29): содержимое само задаёт поля — блоку
   * пользователя нужна линия на всю ширину панели.
   */
  flush?: boolean;
}

function SidebarFooter({ children, flush = false }: SidebarFooterProps) {
  return <div className={flush ? "shrink-0 mt-auto" : "shrink-0 px-4 py-3 mt-auto"}>{children}</div>;
}

function Sidebar({ children }: SlotProps) {
  const { menuInDrawer, sidebarOpen, closeSidebar } = useAppShell();

  if (menuInDrawer) {
    return (
      <Sheet open={sidebarOpen} onOpenChange={(o) => !o && closeSidebar()}>
        <SheetContent
          side="left"
          className="w-72 max-w-[85vw] bg-sidebar p-0 flex flex-col gap-0"
          // Метка шторки-меню: тема кабинета (index.css) красит крестик под панель.
          data-cabinet-sidebar=""
          showClose
        >
          {children}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className={cn(
        "fixed bottom-0 left-0 z-40 flex flex-col bg-sidebar",
        "[box-shadow:inset_-1px_0_0_var(--sidebar-edge)]",
      )}
      style={{ width: SIDEBAR_WIDTH, top: SHELL_TOP }}
    >
      {children}
    </aside>
  );
}

interface HeaderProps extends SlotProps {
  /**
   * Выровнять содержимое шапки по колонке страницы (phase19.29, кабинет
   * платформы): та же максимальная ширина, центрирование и боковые отступы,
   * что у `Content`; в режиме «на всю ширину» (fluid) — тоже на всю ширину.
   * Фон и нижняя линия шапки остаются на всю ширину. Без флага — прежняя
   * шапка (кабинет магазина).
   */
  aligned?: boolean;
}

function Header({ children, aligned = false }: HeaderProps) {
  const { menuInDrawer, fluid, headerSize } = useAppShell();
  const tall = headerSize === "tall";
  return (
    <header
      className={cn(
        // Фон шапки непрозрачный (дефект приёмки Б12: под «стеклом» на
        // телефоне просвечивал прокручиваемый текст страницы).
        "fixed right-0 z-30 bg-[var(--surface-bright)]",
        HEADER_SIZE_CLASS[headerSize].header,
        "[box-shadow:inset_0_-1px_0_var(--outline-variant)]",
        aligned ? "flex items-center" : "flex items-center gap-4 px-4 sm:px-6",
        menuInDrawer ? "left-0" : "",
      )}
      style={{
        // Высокая шапка задаёт высоту классами по ширине экрана.
        minHeight: tall ? undefined : HEADER_HEIGHT_PX,
        left: menuInDrawer ? 0 : SIDEBAR_WIDTH,
        top: SHELL_TOP,
      }}
    >
      {aligned ? (
        <div
          className={cn("flex h-full w-full min-w-0 items-center gap-4", CONTENT_GUTTER_X, fluid ? "" : "mx-auto")}
          style={fluid ? undefined : { maxWidth: CONTENT_MAX_WIDTH_PX }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </header>
  );
}

function Content({ children }: SlotProps) {
  const { menuInDrawer, fluid, plain, bleed, headerSize } = useAppShell();
  return (
    <main
      className={cn("min-h-screen bg-background", HEADER_SIZE_CLASS[headerSize].content)}
      style={
        {
          marginLeft: menuInDrawer ? 0 : SIDEBAR_WIDTH,
          marginTop: SHELL_TOP,
          // Левый край контента для `fixed`-потомков (нижняя полоса действия).
          // Пользовательское свойство наследуется по дереву, а не по
          // containing block, поэтому полоса с `position: fixed` узнаёт про
          // постоянный сайдбар и не уезжает под него на 768px (дефект QA 5.1).
          "--app-bar-left": menuInDrawer ? "0px" : SIDEBAR_WIDTH,
        } as CSSProperties
      }
    >
      {/* Фон «шёлк» (SC66): первым ребёнком — над фоном <main>, под содержимым. */}
      <CabinetBackdrop off={fluid || plain} />
      <div
        className={cn(
          // relative — содержимое рисуется после закреплённого фона, то есть поверх него.
          "relative w-full",
          bleed ? "" : CONTENT_GUTTER_X,
          bleed ? "" : CONTENT_GUTTER_Y,
          fluid || bleed ? "" : "mx-auto",
        )}
        style={fluid || bleed ? undefined : { maxWidth: CONTENT_MAX_WIDTH_PX }}
      >
        {children}
      </div>
    </main>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

interface AppShellProps {
  children: ReactNode;
  /** С какой ширины закреплять боковую панель; по умолчанию md (как раньше). */
  pinSidebarAt?: SidebarPin;
  headerSize?: HeaderSize;
  /** Закреплённая полоса над шапкой (вход под сотрудником); нет — нет полосы. */
  banner?: ReactNode;
}

function AppShellRoot({ children, pinSidebarAt = "md", headerSize = "default", banner }: AppShellProps) {
  const isPhone = useMediaQuery(PHONE_QUERY);
  const belowLg = useMediaQuery(BELOW_LG_QUERY);
  const isMobile = pinSidebarAt === "lg" ? belowLg : isPhone;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fluid, setFluid] = useState(false);
  const [plain, setPlain] = useState(false);
  const [drawerMenu, setDrawerMenu] = useState(false);
  const [bleed, setBleed] = useState(false);
  const bannerBox = useBannerHeight(!!banner);

  const value = useMemo<AppShellContextValue>(
    () => ({
      isMobile,
      isPhone,
      headerSize,
      sidebarOpen,
      toggleSidebar: () => setSidebarOpen((open) => !open),
      closeSidebar: () => setSidebarOpen(false),
      fluid,
      setFluid,
      plain,
      setPlain,
      menuInDrawer: isMobile || drawerMenu,
      setDrawerMenu,
      bleed,
      setBleed,
    }),
    [isMobile, isPhone, headerSize, sidebarOpen, fluid, plain, drawerMenu, bleed],
  );

  return (
    <AppShellContext.Provider value={value}>
      <div
        className={cn("min-h-screen bg-background", HEADER_SIZE_CLASS[headerSize].vars, STICKY_TOP_VAR)}
        style={banner ? ({ "--shell-top": bannerBox.height ?? BANNER_HEIGHT } as CSSProperties) : undefined}
      >
        {banner ? (
          <div ref={bannerBox.ref} className="fixed inset-x-0 top-0 z-40" style={{ minHeight: BANNER_HEIGHT }}>
            {banner}
          </div>
        ) : null}
        {children}
      </div>
    </AppShellContext.Provider>
  );
}

/**
 * Фактическая высота полосы над шапкой (phase19.31, дефект приёмки
 * 2026-10-02): текст полосы входа под сотрудником переносится на вторую
 * строку вместо многоточия, и полоса растёт. Высоту меряем и отдаём в
 * `--shell-top` — шапка, боковая панель, содержимое и липкие элементы
 * (`--shell-sticky-top`) сдвигаются вместе с ней. Без наблюдателя размеров
 * (тесты) — прежние 3rem.
 */
function useBannerHeight(enabled: boolean) {
  const [height, setHeight] = useState<string | null>(null);
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!enabled || !el || typeof ResizeObserver === "undefined") return;
    const measure = () => setHeight(`${Math.ceil(el.getBoundingClientRect().height)}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, el]);
  return { ref: setEl, height: enabled ? height : null };
}

/** Пустой переключатель — для страниц под замком ширины. */
const noop = () => {};

/**
 * Замок ширины (phase19.31): страницы внутри не могут включить режим «на
 * всю ширину» (`useFluidLayout` у них ничего не делает). Нужен каркасам,
 * которые сами задают одну ширину всем вложенным страницам, — «Настройки»:
 * левый край и ширина раздела не должны прыгать при переходе между
 * разделами.
 */
function FluidLock({ children }: SlotProps) {
  const shell = useAppShellOptional();
  const value = useMemo<AppShellContextValue | null>(
    () => (shell ? { ...shell, fluid: false, setFluid: noop } : null),
    [shell],
  );
  // Вне каркаса (тесты страниц) запирать нечего.
  if (!value) return <>{children}</>;
  return <AppShellContext.Provider value={value}>{children}</AppShellContext.Provider>;
}

/**
 * Включает fluid-layout для текущей страницы (на mount → setFluid(true),
 * на unmount → setFluid(false)). Используется на Канбане и любой страницы,
 * где нужна вся ширина контентной зоны.
 */
export function useFluidLayout(): void {
  const { setFluid } = useAppShell();
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);
}

export const AppShell = Object.assign(AppShellRoot, {
  Sidebar,
  SidebarHeader,
  SidebarNav,
  SidebarFooter,
  Header,
  Content,
  FluidLock,
});
